import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { errorMessage } from "../lib/api";
import { PageHeader, ActionButton, GhostButton, FormField, inputClass } from "../components/Primitives";
import { Modal } from "../components/Modal";
import CompositeHealthCard from "../components/CompositeHealthCard";
import MetricStatusPill from "../components/MetricStatusPill";
import PortalTemplatesBar from "../components/PortalTemplatesBar";
import ChipListEditor from "../components/ChipListEditor";
import {
  ArrowLeft, Save, ArrowUp, ArrowDown, Eye, RotateCcw, Info, GripVertical,
  Plus, Trash2, MessageSquarePlus, FileText, Wrench, AlertTriangle,
} from "lucide-react";

const DIRECTION_OPTIONS = [
  { value: "higher_better", label: "Higher is better" },
  { value: "lower_better",  label: "Lower is better" },
];

const APPROVAL_KINDS = [
  { key: "incident",    label: "Incidents",           kind: "severity" },
  { key: "dar",         label: "Daily activity reports", kind: "dar" },
  { key: "maintenance", label: "Maintenance requests", kind: "severity" },
];

const SEVERITY_OPTIONS = [
  { value: "low",      label: "Low or above (all)" },
  { value: "medium",   label: "Medium or above" },
  { value: "high",     label: "High or above" },
  { value: "critical", label: "Critical only" },
];

const DAR_TRIGGER_OPTIONS = [
  { value: "always",             label: "Every DAR" },
  { value: "on_maintenance",     label: "Only when maintenance issues logged" },
  { value: "on_notable_events",  label: "Only when notable events logged" },
];

/**
 * Admin page — per-client Portal Configuration.
 * Route: /clients/:clientId/portal-config
 *
 * Controls:
 *   1. Reorder metric modules (up/down arrows)
 *   2. Toggle visibility per module
 *   3. Rename label + tooltip per module
 *   4. Assign section (Coverage / Response / Quality — or custom)
 *   5. Green/yellow thresholds per module
 *   6. Health-score weights (Coverage / Checkpoint / Response, defaults 60/30/10)
 *   7. Per-site threshold overrides
 *   8. "View as client" preview modal
 */
export default function AdminClientPortalConfig() {
  const { clientId } = useParams();
  const nav = useNavigate();
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [client, setClient]     = useState(null);
  const [config, setConfig]     = useState(null);
  const [dirty, setDirty]       = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview]   = useState(null);
  const [sites, setSites]       = useState([]);

  // Form-overrides state — per-client override of the agency-wide DAR/IR/MR
  // quick chips and incident-type catalogue. See backend
  // /api/clients/{id}/form-overrides + /api/agency/operational-settings.
  const [formOv, setFormOv]           = useState({});          // draft (only overridden keys present)
  const [formOvInitial, setFormOvInitial] = useState({});      // server snapshot
  const [agencyForms, setAgencyForms] = useState(null);        // agency defaults for preview
  const [formTab, setFormTab]         = useState("dar");
  const [formSaving, setFormSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: r }, { data: c }, { data: s }, { data: fo }, { data: forms }, { data: itypes }] = await Promise.all([
        api.get(`/clients/${clientId}/portal-config`),
        api.get(`/clients/${clientId}/detail`).catch(() => ({ data: null })),
        api.get(`/sites`, { params: { client_id: clientId } }).catch(() => ({ data: [] })),
        api.get(`/clients/${clientId}/form-overrides`).catch(() => ({ data: {} })),
        api.get(`/agency/form-quick-phrases`).catch(() => ({ data: null })),
        api.get(`/portal/incident-types`).catch(() => ({ data: null })),
      ]);
      setConfig(r.config);
      setClient(c?.client || c);
      setSites(Array.isArray(s) ? s : (s.items || []));
      setFormOv(fo || {});
      setFormOvInitial(fo || {});
      // Fold both endpoints into a single { dar_quick_phrases, ir_quick_titles,
      // ir_quick_phrases, mr_quick_titles, mr_quick_phrases, incident_types }
      // shape — the OverrideField preview reads the agency defaults from
      // this combined object.
      setAgencyForms({
        dar_quick_phrases: forms?.dar?.phrases || [],
        ir_quick_titles:   forms?.ir?.titles   || [],
        ir_quick_phrases:  forms?.ir?.phrases  || [],
        mr_quick_titles:   forms?.mr?.titles   || [],
        mr_quick_phrases:  forms?.mr?.phrases  || [],
        incident_types:    itypes?.types       || [],
      });
      setDirty(false);
    } catch (err) {
      toast.error(errorMessage(err, "Failed to load portal config"));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const patchMetric = (key, updater) => {
    setConfig((c) => {
      const next = { ...c, metrics: c.metrics.map((m) => (m.key === key ? updater({ ...m }) : m)) };
      return next;
    });
    setDirty(true);
  };

  const moveMetric = (key, dir) => {
    setConfig((c) => {
      const arr = [...c.metrics];
      const idx = arr.findIndex((m) => m.key === key);
      const to = idx + dir;
      if (idx < 0 || to < 0 || to >= arr.length) return c;
      [arr[idx], arr[to]] = [arr[to], arr[idx]];
      return { ...c, metrics: arr.map((m, i) => ({ ...m, order: i })) };
    });
    setDirty(true);
  };

  // Drag-to-reorder — moves `fromKey` to sit immediately before `toKey`
  // in the flat metrics array. Cross-section drop supported: when the target
  // metric lives in a different section, the moved metric adopts that section
  // so it renders under the new heading.
  const reorderMetric = (fromKey, toKey) => {
    if (!fromKey || !toKey || fromKey === toKey) return;
    setConfig((c) => {
      const arr = [...c.metrics];
      const from = arr.findIndex((m) => m.key === fromKey);
      const to = arr.findIndex((m) => m.key === toKey);
      if (from < 0 || to < 0) return c;
      const [moved] = arr.splice(from, 1);
      const targetSection = arr[to]?.section;
      if (targetSection && moved.section !== targetSection) {
        moved.section = targetSection;
      }
      arr.splice(to, 0, moved);
      return { ...c, metrics: arr.map((m, i) => ({ ...m, order: i })) };
    });
    setDirty(true);
  };

  // Drop-on-section-header — moves the metric to the END of the target section
  // and reassigns its `section` field. Allows dropping into empty sections too.
  const moveMetricToSection = (fromKey, sectionKey) => {
    if (!fromKey || !sectionKey) return;
    setConfig((c) => {
      const arr = [...c.metrics];
      const from = arr.findIndex((m) => m.key === fromKey);
      if (from < 0) return c;
      const [moved] = arr.splice(from, 1);
      if (moved.section === sectionKey) {
        // Same-section drop → put at the end of the group.
        moved.section = sectionKey;
      } else {
        moved.section = sectionKey;
      }
      // Insert after the last metric currently in `sectionKey`.
      let insertAt = arr.length;
      for (let i = arr.length - 1; i >= 0; i -= 1) {
        if (arr[i].section === sectionKey) { insertAt = i + 1; break; }
      }
      arr.splice(insertAt, 0, moved);
      return { ...c, metrics: arr.map((m, i) => ({ ...m, order: i })) };
    });
    setDirty(true);
  };

  const setApprovalOverride = (kind, patch) => {
    setConfig((c) => {
      const cur = { ...(c.approval_overrides || {}) };
      // patch===null clears the override for that kind (falls back to agency default)
      if (patch === null) delete cur[kind];
      else cur[kind] = { ...(cur[kind] || {}), ...patch };
      return { ...c, approval_overrides: cur };
    });
    setDirty(true);
  };

  const patchWeight = (k, val) => {
    setConfig((c) => ({ ...c, health_weights: { ...(c.health_weights || {}), [k]: Number(val) } }));
    setDirty(true);
  };

  const normalizeWeights = () => {
    // Proportionally scale the 4 weights so they sum to 100. Round each and
    // dump any residual on the largest slot to guarantee an exact 100 total.
    const keys = ["coverage", "checkpoint", "response", "supervisor"];
    const cur = keys.map((k) => Math.max(0, Number((config?.health_weights || {})[k]) || 0));
    const total = cur.reduce((a, b) => a + b, 0);
    if (total === 0) {
      // All zeros → seed defaults instead of dividing by zero.
      setConfig((c) => ({ ...c, health_weights: { coverage: 55, checkpoint: 25, response: 10, supervisor: 10 } }));
      setDirty(true);
      return;
    }
    const scaled = cur.map((v) => Math.round((v / total) * 100));
    const drift = 100 - scaled.reduce((a, b) => a + b, 0);
    if (drift !== 0) {
      const biggest = scaled.indexOf(Math.max(...scaled));
      scaled[biggest] += drift;
    }
    const next = {};
    keys.forEach((k, i) => { next[k] = scaled[i]; });
    setConfig((c) => ({ ...c, health_weights: next }));
    setDirty(true);
  };

  const upsertSection = (idx, patch) => {
    setConfig((c) => ({ ...c, sections: (c.sections || []).map((s, i) => (i === idx ? { ...s, ...patch } : s)) }));
    setDirty(true);
  };
  const addSection = () => {
    const key = `custom_${Date.now()}`;
    setConfig((c) => ({
      ...c,
      sections: [...(c.sections || []), { key, label: "New section", order: (c.sections || []).length }],
    }));
    setDirty(true);
  };
  const removeSection = (idx) => {
    setConfig((c) => ({ ...c, sections: (c.sections || []).filter((_, i) => i !== idx) }));
    setDirty(true);
  };

  const setSiteOverride = (siteId, metricKey, field, value) => {
    setConfig((c) => {
      const ov = { ...(c.site_overrides || {}) };
      const forSite = { ...(ov[siteId] || {}) };
      const forMetric = { ...(forSite[metricKey] || {}) };
      if (value === "" || value === null) delete forMetric[field];
      else forMetric[field] = Number(value);
      if (Object.keys(forMetric).length === 0) delete forSite[metricKey];
      else forSite[metricKey] = forMetric;
      if (Object.keys(forSite).length === 0) delete ov[siteId];
      else ov[siteId] = forSite;
      return { ...c, site_overrides: ov };
    });
    setDirty(true);
  };

  // ─── Form-override helpers ────────────────────────────────────
  // A key is "overridden" iff it's present in `formOv` (as an array,
  // including empty []). Toggling OFF removes the key so the resolver
  // falls back to the agency default.
  const isOverridden = (key) => Array.isArray(formOv[key]);
  const toggleOverride = (key, enabled) => {
    setFormOv((prev) => {
      const next = { ...prev };
      if (enabled) {
        // Seed with the current agency default so admins can trim from it.
        const seed = Array.isArray(agencyForms?.[key]) ? [...agencyForms[key]] : [];
        next[key] = seed;
      } else {
        delete next[key];
      }
      return next;
    });
  };
  const setOverrideList = (key, list) => {
    setFormOv((prev) => ({ ...prev, [key]: list }));
  };
  const formOvDirty = useMemo(
    () => JSON.stringify(formOv) !== JSON.stringify(formOvInitial),
    [formOv, formOvInitial],
  );
  const saveFormOverrides = async () => {
    setFormSaving(true);
    try {
      // Build payload: for every recognised key, send the array if
      // overridden, or `null` if the admin cleared the override so the
      // server drops it and the cascade falls back to agency defaults.
      const KEYS = [
        "dar_quick_phrases", "ir_quick_titles", "ir_quick_phrases",
        "mr_quick_titles", "mr_quick_phrases", "incident_types",
      ];
      const payload = {};
      for (const k of KEYS) {
        if (Array.isArray(formOv[k])) {
          payload[k] = formOv[k].map((s) => (s || "").trim()).filter(Boolean);
        } else if (formOvInitial[k] !== undefined) {
          // Was overridden, admin toggled off → tell backend to drop it.
          payload[k] = null;
        }
      }
      const { data } = await api.put(`/clients/${clientId}/form-overrides`, payload);
      setFormOv(data || {});
      setFormOvInitial(data || {});
      toast.success("Form overrides saved");
    } catch (err) {
      toast.error(errorMessage(err, "Failed to save form overrides"));
    } finally {
      setFormSaving(false);
    }
  };

  const save = async () => {
    if (!config) return;
    setSaving(true);
    try {
      // Flatten site_overrides to the API's expected list shape.
      const flatOverrides = [];
      for (const [siteId, byMetric] of Object.entries(config.site_overrides || {})) {
        for (const [metricKey, th] of Object.entries(byMetric || {})) {
          if (th && (th.green !== undefined || th.yellow !== undefined)) {
            flatOverrides.push({
              site_id: siteId, metric_key: metricKey,
              green:  th.green  ?? null,
              yellow: th.yellow ?? null,
            });
          }
        }
      }
      const body = {
        metrics: config.metrics.map((m) => ({
          key: m.key, enabled: m.enabled, label: m.label, tooltip: m.tooltip, section: m.section,
          thresholds: {
            direction: m.thresholds?.direction,
            green:     m.thresholds?.green,
            yellow:    m.thresholds?.yellow,
          },
        })),
        sections: config.sections,
        health_weights: config.health_weights,
        site_overrides: flatOverrides,
        approval_overrides: config.approval_overrides || {},
      };
      const { data } = await api.patch(`/clients/${clientId}/portal-config`, body);
      setConfig(data.config);
      setDirty(false);
      toast.success("Client portal config saved");
    } catch (err) {
      toast.error(errorMessage(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const openPreview = async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/portal-config/preview`);
      setPreview(data);
      setPreviewOpen(true);
    } catch (err) {
      toast.error(errorMessage(err, "Preview failed"));
    }
  };

  const weights = config?.health_weights || { coverage: 55, checkpoint: 25, response: 10, supervisor: 10 };
  const weightsTotal = (Number(weights.coverage) || 0) + (Number(weights.checkpoint) || 0) + (Number(weights.response) || 0) + (Number(weights.supervisor) || 0);

  const sectionsByKey = useMemo(() => {
    const map = {};
    (config?.sections || []).forEach((s) => { map[s.key] = s; });
    return map;
  }, [config?.sections]);

  const groupedMetrics = useMemo(() => {
    if (!config) return [];
    const groups = new Map();
    (config.sections || []).forEach((s) => groups.set(s.key, { section: s, metrics: [] }));
    config.metrics.forEach((m) => {
      const key = m.section || "quality";
      if (!groups.has(key)) groups.set(key, { section: { key, label: key, order: 999 }, metrics: [] });
      groups.get(key).metrics.push(m);
    });
    return Array.from(groups.values()).sort((a, b) => (a.section.order || 0) - (b.section.order || 0));
  }, [config]);

  if (loading || !config) {
    return (
      <div className="p-8 text-zinc-400" data-testid="portal-config-loading">Loading…</div>
    );
  }

  return (
    <div data-testid="admin-client-portal-config-page">
      <PageHeader
        title={`Portal Config — ${client?.company_name || "Client"}`}
        subtitle="Configure the client-facing dashboard: which metrics are visible, their labels & tooltips, green/yellow/red thresholds, section grouping, and the composite Site Health Score weights."
        action={
          <div className="flex items-center gap-2">
            <GhostButton data-testid="config-back-btn" onClick={() => nav("/clients")}>
              <ArrowLeft className="size-4" /> Clients
            </GhostButton>
            <GhostButton data-testid="config-preview-btn" onClick={openPreview}>
              <Eye className="size-4" /> View as client
            </GhostButton>
            <ActionButton
              data-testid="config-save-btn"
              onClick={save}
              disabled={!dirty || saving}
              className={!dirty ? "opacity-50 cursor-not-allowed" : ""}
            >
              <Save className="size-4" /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
            </ActionButton>
          </div>
        }
      />

      <div className="p-8 space-y-8">
        {/* ── Templates bar (apply preset / save as template) ─── */}
        <PortalTemplatesBar
          clientId={clientId}
          getSnapshot={() => ({
            sections: (config?.sections || []).map((s) => ({
              key: s.key, label: s.label, order: s.order,
            })),
            metrics: (config?.metrics || []).map((m, i) => ({
              key: m.key,
              enabled: !!m.enabled,
              section: m.section || "quality",
              order: typeof m.order === "number" ? m.order : i,
            })),
          })}
          onApplied={load}
        />

        {/* ── Health score weights ─────────────────────────────── */}
        <div className="border border-[#27272A] bg-[#121212] p-6" data-testid="config-weights-section">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <div className="label-mute">Composite Health Score weights</div>
            <span
              data-testid="weights-total-badge"
              className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 font-mono ${weightsTotal === 100 ? "text-emerald-400 bg-emerald-500/10" : "text-amber-400 bg-amber-500/10"}`}
            >
              Total {weightsTotal}%
            </span>
            {weightsTotal !== 100 && (
              <button
                type="button"
                data-testid="weights-normalize-btn"
                onClick={normalizeWeights}
                title="Rescale the 4 weights proportionally so they sum to 100"
                className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[#27272A] text-zinc-300 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
              >
                Auto-normalize to 100
              </button>
            )}
          </div>
          <div className="text-xs text-zinc-400 mb-4">
            The top-line site-health score is a weighted average of Coverage, Checkpoint compliance, Response, and Supervisor site visits.
            Defaults are 55 / 25 / 10 / 10 — adjust per client vertical (retail vs. residential vs. construction).
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {["coverage", "checkpoint", "response", "supervisor"].map((k) => (
              <WeightSlider
                key={k}
                label={k}
                value={weights[k] ?? 0}
                onChange={(v) => patchWeight(k, v)}
                testId={`weight-${k}`}
              />
            ))}
          </div>
        </div>

        {/* ── Approval overrides (per-client) ────────────────────── */}
        <div className="border border-[#27272A] bg-[#121212] p-6" data-testid="config-approvals-section">
          <div className="flex items-center gap-2 mb-1">
            <div className="label-mute">Approval overrides for this client</div>
          </div>
          <div className="text-xs text-zinc-400 mb-4">
            Override the agency-wide approval rule for just this client. Toggle &quot;Override&quot; ON to write a client-specific rule, OFF to fall back to your agency default from <span className="font-mono">/settings/client-portal</span>.
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {APPROVAL_KINDS.map(({ key: k, label, kind }) => {
              const override = (config.approval_overrides || {})[k];
              const hasOverride = !!override;
              return (
                <div key={k} className="border border-[#27272A] bg-[#0A0A0A] p-4" data-testid={`approval-override-${k}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-zinc-100 text-sm">{label}</div>
                    <label className="flex items-center gap-2 cursor-pointer select-none text-xs">
                      <span className="text-zinc-500">Override</span>
                      <input
                        type="checkbox"
                        className="accent-[#FF3B30]"
                        checked={hasOverride}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setApprovalOverride(k, kind === "dar" ? { enabled: true, when: "always" } : { enabled: true, min_severity: "low" });
                          } else {
                            setApprovalOverride(k, null);
                          }
                        }}
                        data-testid={`approval-override-toggle-${k}`}
                      />
                    </label>
                  </div>
                  {hasOverride ? (
                    <div className="mt-3 space-y-2">
                      <label className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          className="accent-[#FF3B30]"
                          checked={!!override.enabled}
                          onChange={(e) => setApprovalOverride(k, { enabled: e.target.checked })}
                          data-testid={`approval-override-enabled-${k}`}
                        />
                        <span className="text-zinc-300">Approval required</span>
                      </label>
                      {override.enabled && (
                        kind === "severity" ? (
                          <select
                            value={override.min_severity || "low"}
                            onChange={(e) => setApprovalOverride(k, { min_severity: e.target.value })}
                            data-testid={`approval-override-severity-${k}`}
                            className={inputClass}
                          >
                            {SEVERITY_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        ) : (
                          <select
                            value={override.when || "always"}
                            onChange={(e) => setApprovalOverride(k, { when: e.target.value })}
                            data-testid={`approval-override-when-${k}`}
                            className={inputClass}
                          >
                            {DAR_TRIGGER_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        )
                      )}
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-zinc-500 italic">Using agency default</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Section headers ──────────────────────────────────── */}
        <div className="border border-[#27272A] bg-[#121212] p-6" data-testid="config-sections-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-mute">Section headers</div>
              <div className="text-xs text-zinc-400 mt-1">Group metrics under any labels you want — clients see these as headers.</div>
            </div>
            <GhostButton data-testid="add-section-btn" onClick={addSection}><Plus className="size-3.5" /> Add section</GhostButton>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(config.sections || []).map((s, idx) => (
              <div key={s.key} className="border border-[#27272A] bg-[#0A0A0A] p-3 flex items-center gap-2" data-testid={`section-row-${s.key}`}>
                <input
                  className={inputClass}
                  value={s.label}
                  onChange={(e) => upsertSection(idx, { label: e.target.value })}
                  data-testid={`section-label-${s.key}`}
                />
                {(config.sections || []).length > 1 && (
                  <button
                    className="p-1.5 text-zinc-500 hover:text-[#FF3B30]"
                    onClick={() => removeSection(idx)}
                    data-testid={`section-remove-${s.key}`}
                    title="Remove section"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Metric modules ───────────────────────────────────── */}
        <div className="space-y-6" data-testid="config-metrics-section">
          {groupedMetrics.map(({ section, metrics }) => (
            <SectionCard
              key={section.key}
              section={section}
              metrics={metrics}
              onDropOnHeader={(fromKey) => moveMetricToSection(fromKey, section.key)}
            >
              <div className="divide-y divide-[#27272A]">
                {metrics.map((m) => (
                  <MetricRow
                    key={m.key}
                    metric={m}
                    sections={config.sections || []}
                    onPatch={(u) => patchMetric(m.key, u)}
                    onMoveUp={() => moveMetric(m.key, -1)}
                    onMoveDown={() => moveMetric(m.key, +1)}
                    onReorderTo={(toKey) => reorderMetric(m.key, toKey)}
                  />
                ))}
                {metrics.length === 0 && (
                  <div
                    data-testid={`section-empty-${section.key}`}
                    className="p-6 text-center text-zinc-500 text-sm italic"
                  >
                    Drop a metric here to move it into this section.
                  </div>
                )}
              </div>
            </SectionCard>
          ))}
        </div>

        {/* ── Per-site threshold overrides ─────────────────────── */}
        {sites.length > 0 && (
          <div className="border border-[#27272A] bg-[#121212] p-6" data-testid="config-overrides-section">
            <div className="label-mute mb-1">Per-site threshold overrides</div>
            <div className="text-xs text-zinc-400 mb-4">
              Different site profiles (retail vs. construction) may want different green/yellow thresholds.
              Overrides here beat the client-level defaults above for that specific site.
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[#27272A]">
                    <th className="py-2 pr-4">Site</th>
                    <th className="py-2 pr-4">Metric</th>
                    <th className="py-2 pr-4">Client default</th>
                    <th className="py-2 pr-4">Green ≥ / ≤</th>
                    <th className="py-2 pr-4">Yellow ≥ / ≤</th>
                    <th className="py-2 pr-4">Reset</th>
                  </tr>
                </thead>
                <tbody>
                  {sites.slice(0, 20).flatMap((site) =>
                    (config.metrics || []).filter((m) => m.enabled).map((m) => {
                      const override = ((config.site_overrides || {})[site.id] || {})[m.key] || {};
                      const hasOverride = override.green !== undefined || override.yellow !== undefined;
                      return (
                        <tr key={`${site.id}-${m.key}`} className={`border-b border-[#1F1F1F] ${hasOverride ? "bg-[#0F1F1F]" : ""}`}>
                          <td className="py-2 pr-4 text-zinc-300 truncate max-w-[140px]" title={site.name}>{site.name}</td>
                          <td className="py-2 pr-4 text-zinc-300 truncate max-w-[160px]" title={m.label}>{m.label}</td>
                          <td className="py-2 pr-4 text-zinc-500 font-mono text-xs whitespace-nowrap">
                            {m.thresholds?.direction === "lower_better" ? "≤ " : "≥ "}
                            {m.thresholds?.green} / {m.thresholds?.yellow}
                          </td>
                          <td className="py-2 pr-4">
                            <input
                              type="number" step="0.1"
                              className={`${inputClass} w-24 py-1 text-xs`}
                              value={override.green ?? ""}
                              onChange={(e) => setSiteOverride(site.id, m.key, "green", e.target.value)}
                              data-testid={`override-green-${site.id}-${m.key}`}
                            />
                          </td>
                          <td className="py-2 pr-4">
                            <input
                              type="number" step="0.1"
                              className={`${inputClass} w-24 py-1 text-xs`}
                              value={override.yellow ?? ""}
                              onChange={(e) => setSiteOverride(site.id, m.key, "yellow", e.target.value)}
                              data-testid={`override-yellow-${site.id}-${m.key}`}
                            />
                          </td>
                          <td className="py-2 pr-4">
                            {hasOverride && (
                              <button
                                className="p-1 text-zinc-500 hover:text-[#FF3B30]"
                                onClick={() => { setSiteOverride(site.id, m.key, "green", ""); setSiteOverride(site.id, m.key, "yellow", ""); }}
                                data-testid={`override-reset-${site.id}-${m.key}`}
                                title="Reset override"
                              >
                                <RotateCcw className="size-3.5" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
              {sites.length > 20 && (
                <div className="text-xs text-zinc-500 mt-3">
                  Showing first 20 sites × visible metrics. Filter/search coming in a follow-up.
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Form overrides (per-client) — DAR/IR/MR chips + incidents ── */}
        <FormOverridesSection
          formOv={formOv}
          agencyForms={agencyForms}
          formTab={formTab}
          setFormTab={setFormTab}
          isOverridden={isOverridden}
          toggleOverride={toggleOverride}
          setOverrideList={setOverrideList}
          onSave={saveFormOverrides}
          onRevert={() => setFormOv(JSON.parse(JSON.stringify(formOvInitial)))}
          dirty={formOvDirty}
          saving={formSaving}
        />
      </div>

      {/* Preview modal */}
      {previewOpen && (
        <Modal
          onClose={() => setPreviewOpen(false)}
          title="Client-view preview"
          wide
          testId="preview-modal"
        >
          <ClientPreview preview={preview} client={client} />
        </Modal>
      )}
    </div>
  );
}


function WeightSlider({ label, value, onChange, testId }) {
  return (
    <div className="border border-[#27272A] bg-[#0A0A0A] p-4">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">{label}</div>
        <div className="font-mono text-2xl text-[#FF3B30]" data-testid={`${testId}-value`}>{Math.round(value)}%</div>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        step={5}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-2 accent-[#FF3B30]"
        data-testid={testId}
      />
    </div>
  );
}


/**
 * SectionCard — wraps a metric section with a drop-target header so admins
 * can drag a metric onto the header to reassign it to this section (and
 * park it at the end of the group).
 */
function SectionCard({ section, metrics, onDropOnHeader, children }) {
  const [over, setOver] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (!over) setOver(true);
  };
  const handleDragLeave = () => setOver(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setOver(false);
    let fromKey = "";
    try { fromKey = e.dataTransfer.getData("text/plain"); } catch { fromKey = ""; }
    if (fromKey && typeof onDropOnHeader === "function") onDropOnHeader(fromKey);
  };

  return (
    <div
      className={`border bg-[#121212] transition-colors ${
        over ? "border-[#FF3B30]" : "border-[#27272A]"
      }`}
      data-testid={`section-card-${section.key}`}
    >
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`px-6 py-3 border-b flex items-center gap-2 transition-colors ${
          over ? "border-[#FF3B30] bg-[#FF3B30]/10" : "border-[#27272A]"
        }`}
        data-testid={`section-header-${section.key}`}
      >
        <div className="font-display uppercase tracking-widest text-sm text-[#FF3B30] font-bold">
          {section.label}
        </div>
        <div className="label-mute">· {metrics.length} module{metrics.length === 1 ? "" : "s"}</div>
        {over && (
          <div className="ml-auto text-[10px] uppercase tracking-widest text-[#FF3B30] font-bold animate-pulse">
            Drop to move here
          </div>
        )}
      </div>
      {children}
    </div>
  );
}


function MetricRow({ metric, sections, onPatch, onMoveUp, onMoveDown, onReorderTo }) {
  const disabled = !metric.enabled;
  const [dragOver, setDragOver] = useState(false);

  const handleDragStart = (e) => {
    try { e.dataTransfer.setData("text/plain", metric.key); } catch { /* no-op */ }
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (!dragOver) setDragOver(true);
  };
  const handleDragLeave = () => setDragOver(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    let fromKey = "";
    try { fromKey = e.dataTransfer.getData("text/plain"); } catch { fromKey = ""; }
    if (fromKey && fromKey !== metric.key && typeof onReorderTo === "function") {
      onReorderTo(fromKey);
    }
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onDragEnd={() => setDragOver(false)}
      className={`p-4 grid grid-cols-1 lg:grid-cols-12 gap-3 items-start transition-colors ${
        disabled ? "opacity-60" : ""
      } ${dragOver ? "bg-[#FF3B30]/5 outline outline-2 outline-[#FF3B30]" : ""}`}
      data-testid={`metric-row-${metric.key}`}
    >
      {/* Drag handle + up/down fallback for keyboard users */}
      <div className="lg:col-span-1 flex lg:flex-col items-center gap-1">
        <button
          className="p-1 text-zinc-500 hover:text-zinc-200 cursor-grab active:cursor-grabbing"
          title="Drag to reorder"
          data-testid={`metric-drag-handle-${metric.key}`}
          onMouseDown={(e) => e.currentTarget.blur()}
        >
          <GripVertical className="size-4" />
        </button>
        <button className="p-1 text-zinc-500 hover:text-zinc-200" onClick={onMoveUp} data-testid={`metric-move-up-${metric.key}`} title="Move up"><ArrowUp className="size-3.5" /></button>
        <button className="p-1 text-zinc-500 hover:text-zinc-200" onClick={onMoveDown} data-testid={`metric-move-down-${metric.key}`} title="Move down"><ArrowDown className="size-3.5" /></button>
      </div>

      {/* Key + Implemented pill */}
      <div className="lg:col-span-3 min-w-0">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={!!metric.enabled}
            onChange={(e) => onPatch((m) => ({ ...m, enabled: e.target.checked }))}
            className="accent-[#FF3B30] size-4"
            data-testid={`metric-enabled-${metric.key}`}
          />
          <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 truncate">{metric.key}</div>
        </div>
        {!metric.implemented && (
          <span className="inline-block mt-1 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-amber-400 border border-amber-500/40 bg-amber-500/10">
            Coming soon
          </span>
        )}
        <input
          className={`${inputClass} mt-2 text-sm`}
          value={metric.label}
          onChange={(e) => onPatch((m) => ({ ...m, label: e.target.value }))}
          placeholder="Display label"
          data-testid={`metric-label-${metric.key}`}
        />
        <input
          className={`${inputClass} mt-2 text-xs`}
          value={metric.tooltip || ""}
          onChange={(e) => onPatch((m) => ({ ...m, tooltip: e.target.value }))}
          placeholder="Optional tooltip / help text"
          data-testid={`metric-tooltip-${metric.key}`}
        />
      </div>

      {/* Section */}
      <div className="lg:col-span-2">
        <FormField label="Section">
          <select
            className={inputClass}
            value={metric.section}
            onChange={(e) => onPatch((m) => ({ ...m, section: e.target.value }))}
            data-testid={`metric-section-${metric.key}`}
          >
            {sections.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </FormField>
      </div>

      {/* Direction */}
      <div className="lg:col-span-2">
        <FormField label="Direction">
          <select
            className={inputClass}
            value={metric.thresholds?.direction || "higher_better"}
            onChange={(e) => onPatch((m) => ({ ...m, thresholds: { ...(m.thresholds || {}), direction: e.target.value } }))}
            data-testid={`metric-direction-${metric.key}`}
          >
            {DIRECTION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </FormField>
      </div>

      {/* Green / Yellow */}
      <div className="lg:col-span-2">
        <FormField label="Green threshold">
          <input
            type="number" step="0.1"
            className={inputClass}
            value={metric.thresholds?.green ?? ""}
            onChange={(e) => onPatch((m) => ({ ...m, thresholds: { ...(m.thresholds || {}), green: e.target.value === "" ? null : Number(e.target.value) } }))}
            data-testid={`metric-green-${metric.key}`}
          />
        </FormField>
      </div>
      <div className="lg:col-span-2">
        <FormField label="Yellow threshold">
          <input
            type="number" step="0.1"
            className={inputClass}
            value={metric.thresholds?.yellow ?? ""}
            onChange={(e) => onPatch((m) => ({ ...m, thresholds: { ...(m.thresholds || {}), yellow: e.target.value === "" ? null : Number(e.target.value) } }))}
            data-testid={`metric-yellow-${metric.key}`}
          />
        </FormField>
      </div>
    </div>
  );
}


function ClientPreview({ preview, client }) {
  if (!preview) return <div className="text-zinc-400 p-6">No preview data.</div>;
  const enabled = preview.metrics.filter((m) => m.enabled);
  const sections = preview.sections || [];
  return (
    <div className="space-y-4 p-2" data-testid="preview-body">
      <div className="text-xs text-zinc-500">Preview for <span className="text-zinc-300 font-medium">{client?.company_name}</span> — this is the exact metric set + labels the client sees on their dashboard.</div>
      <CompositeHealthCard
        score={72}
        status="yellow"
        breakdown={[
          { name: "coverage",   status: "green",   weight: preview.health_weights?.coverage   ?? 55 },
          { name: "checkpoint", status: "yellow",  weight: preview.health_weights?.checkpoint ?? 25 },
          { name: "response",   status: "unknown", weight: preview.health_weights?.response   ?? 10 },
          { name: "supervisor", status: "green",   weight: preview.health_weights?.supervisor ?? 10 },
        ]}
      />
      {sections.map((s) => {
        const rows = enabled.filter((m) => m.section === s.key);
        if (!rows.length) return null;
        return (
          <div key={s.key} className="border border-[#27272A]">
            <div className="px-4 py-2 border-b border-[#27272A] label-mute">{s.label}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
              {rows.map((m) => (
                <div key={m.key} className="border border-[#27272A] bg-[#0A0A0A] p-3">
                  <div className="flex items-start justify-between">
                    <div className="label-mute truncate pr-2" title={m.label}>{m.label}</div>
                    <MetricStatusPill status="green" testId={`preview-status-${m.key}`} />
                  </div>
                  {m.tooltip && (
                    <div className="text-[11px] text-zinc-500 mt-2 flex items-start gap-1">
                      <Info className="size-3 flex-shrink-0 mt-0.5" /><span>{m.tooltip}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}



/**
 * FormOverridesSection — per-client override for the DAR / IR / MR
 * quick-chip catalogues + Incident-type list. Each field has an
 * "Override" toggle:
 *   OFF → guards see the agency defaults (rendered read-only as a preview)
 *   ON  → guards see the client-specific list edited here
 *
 * The section owns its own dirty state + Save button because it hits a
 * different endpoint (`PUT /clients/{id}/form-overrides`) than the
 * portal-config Save button up top.
 */
function FormOverridesSection({
  formOv, agencyForms, formTab, setFormTab,
  isOverridden, toggleOverride, setOverrideList,
  onSave, onRevert, dirty, saving,
}) {
  const TABS = [
    { key: "dar", label: "DAR",       icon: MessageSquarePlus },
    { key: "ir",  label: "Incidents", icon: FileText },
    { key: "mr",  label: "Maintenance", icon: Wrench },
    { key: "types", label: "Incident types", icon: AlertTriangle },
  ];

  return (
    <div className="border border-[#27272A] bg-[#121212] p-6" data-testid="form-overrides-section">
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <div>
          <div className="label-mute">Form overrides for this client</div>
          <div className="text-xs text-zinc-400 mt-1">
            Override the agency-wide quick-chip catalogues just for this client&apos;s
            . Toggle <span className="text-zinc-300">Override</span> ON on any
            field to write a client-specific list, OFF to fall back to your
            agency default from <span className="font-mono">/settings/client-portal</span>.
          </div>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <GhostButton onClick={onRevert} disabled={saving} data-testid="form-overrides-revert-btn">
              <RotateCcw className="size-3.5" /> Revert
            </GhostButton>
          )}
          <ActionButton
            data-testid="form-overrides-save-btn"
            onClick={onSave}
            disabled={!dirty || saving}
            className={!dirty ? "opacity-50 cursor-not-allowed" : ""}
          >
            <Save className="size-4" /> {saving ? "Saving…" : dirty ? "Save overrides" : "Saved"}
          </ActionButton>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 mt-4 mb-4 border-b border-[#27272A]" data-testid="form-overrides-tabs">
        {TABS.map(({ key, label, icon: Icon }) => {
          const active = formTab === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setFormTab(key)}
              data-testid={`form-overrides-tab-${key}`}
              className={`px-3 py-2 text-xs uppercase tracking-widest font-bold border-b-2 -mb-px inline-flex items-center gap-2 transition-colors ${
                active
                  ? "text-[#FF3B30] border-[#FF3B30]"
                  : "text-zinc-500 border-transparent hover:text-zinc-200"
              }`}
            >
              <Icon className="size-3.5" />
              {label}
            </button>
          );
        })}
      </div>

      {formTab === "dar" && (
        <OverrideField
          fieldKey="dar_quick_phrases"
          label="DAR quick-phrase presets"
          hint="Officers see these as tappable chips above the DAR narrative field on this client's sites. Same {slot_name} placeholder syntax as the agency defaults."
          placeholder="e.g. Officer completed all scheduled scans on time."
          rows={2}
          showSlots
          cap={12}
          agencyList={agencyForms?.dar_quick_phrases}
          overridden={isOverridden("dar_quick_phrases")}
          overrideList={formOv.dar_quick_phrases}
          onToggle={(v) => toggleOverride("dar_quick_phrases", v)}
          onChange={(next) => setOverrideList("dar_quick_phrases", next)}
        />
      )}

      {formTab === "ir" && (
        <div className="space-y-4">
          <OverrideField
            fieldKey="ir_quick_titles"
            label="IR title chips"
            hint="One-tap set the IR title on this client's sites."
            placeholder="e.g. Suspicious person"
            rows={1}
            cap={12}
            agencyList={agencyForms?.ir_quick_titles}
            overridden={isOverridden("ir_quick_titles")}
            overrideList={formOv.ir_quick_titles}
            onToggle={(v) => toggleOverride("ir_quick_titles", v)}
            onChange={(next) => setOverrideList("ir_quick_titles", next)}
          />
          <OverrideField
            fieldKey="ir_quick_phrases"
            label="IR narrative chips"
            hint="Append-style templates for the IR narrative field. Use {slot_name} to prompt  for fill-ins."
            placeholder="e.g. Observed an individual at {location} at ~{time}."
            rows={2}
            showSlots
            cap={12}
            agencyList={agencyForms?.ir_quick_phrases}
            overridden={isOverridden("ir_quick_phrases")}
            overrideList={formOv.ir_quick_phrases}
            onToggle={(v) => toggleOverride("ir_quick_phrases", v)}
            onChange={(next) => setOverrideList("ir_quick_phrases", next)}
          />
        </div>
      )}

      {formTab === "mr" && (
        <div className="space-y-4">
          <OverrideField
            fieldKey="mr_quick_titles"
            label="MR title chips"
            hint="One-tap set the Maintenance Request title on this client's sites."
            placeholder="e.g. Broken lock / hardware"
            rows={1}
            cap={12}
            agencyList={agencyForms?.mr_quick_titles}
            overridden={isOverridden("mr_quick_titles")}
            overrideList={formOv.mr_quick_titles}
            onToggle={(v) => toggleOverride("mr_quick_titles", v)}
            onChange={(next) => setOverrideList("mr_quick_titles", next)}
          />
          <OverrideField
            fieldKey="mr_quick_phrases"
            label="MR description chips"
            hint="Append-style templates for the MR description field. Same {slot} syntax as DAR / IR."
            placeholder="e.g. Discovered issue during patrol at {location}."
            rows={2}
            showSlots
            cap={12}
            agencyList={agencyForms?.mr_quick_phrases}
            overridden={isOverridden("mr_quick_phrases")}
            overrideList={formOv.mr_quick_phrases}
            onToggle={(v) => toggleOverride("mr_quick_phrases", v)}
            onChange={(next) => setOverrideList("mr_quick_phrases", next)}
          />
        </div>
      )}

      {formTab === "types" && (
        <OverrideField
          fieldKey="incident_types"
          label="Incident type catalogue"
          hint="The picker at the top of every IR — client-specific vocabulary. Up to 30 entries."
          placeholder="e.g. Trespassing"
          rows={1}
          cap={30}
          agencyList={agencyForms?.incident_types}
          overridden={isOverridden("incident_types")}
          overrideList={formOv.incident_types}
          onToggle={(v) => toggleOverride("incident_types", v)}
          onChange={(next) => setOverrideList("incident_types", next)}
        />
      )}
    </div>
  );
}


/**
 * OverrideField — one row of the FormOverridesSection. Renders an
 * "Override" toggle + either the editable ChipListEditor (when ON) or a
 * grayed-out read-only preview of the agency default (when OFF).
 */
function OverrideField({
  fieldKey, label, hint, placeholder, rows, showSlots, cap,
  agencyList, overridden, overrideList, onToggle, onChange,
}) {
  const agency = Array.isArray(agencyList) ? agencyList : [];
  return (
    <div className="border border-[#27272A] bg-[#0A0A0A]" data-testid={`form-override-${fieldKey}`}>
      <div className="px-5 py-4 border-b border-[#27272A] flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium text-zinc-100 text-sm">{label}</div>
          <div className="text-xs text-zinc-500 mt-1">{hint}</div>
          <div className="text-[10px] uppercase tracking-widest mt-2 font-mono">
            {overridden ? (
              <span className="text-[#FF3B30]">Client override active</span>
            ) : (
              <span className="text-zinc-500">
                Using agency default · {agency.length} entr{agency.length === 1 ? "y" : "ies"}
              </span>
            )}
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none text-xs whitespace-nowrap">
          <span className="text-zinc-500 uppercase tracking-widest text-[10px] font-bold">Override</span>
          <input
            type="checkbox"
            className="accent-[#FF3B30] size-4"
            checked={overridden}
            onChange={(e) => onToggle(e.target.checked)}
            data-testid={`form-override-toggle-${fieldKey}`}
          />
        </label>
      </div>
      {overridden ? (
        <div className="p-3">
          <ChipListEditor
            testIdPrefix={`form-override-editor-${fieldKey}`}
            label="Client-specific list"
            hint="Officers on this client's sites see this list instead of the agency default."
            values={overrideList || []}
            onChange={onChange}
            rows={rows}
            placeholder={placeholder}
            showSlots={showSlots}
            cap={cap}
          />
        </div>
      ) : (
        <div className="p-5" data-testid={`form-override-preview-${fieldKey}`}>
          {agency.length === 0 ? (
            <div className="text-xs text-zinc-500 italic">Agency default is empty for this field.</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {agency.map((v, i) => (
                <span
                  key={i}
                  className="px-2 py-1 text-xs text-zinc-500 border border-[#27272A] bg-[#0A0A0A] font-mono"
                  title={v}
                >
                  {v.length > 60 ? v.slice(0, 57) + "…" : v}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
